import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { activeClientState } from '../../state.js';
import { api } from '../../api.js';
import { useApi } from '../../hooks.js';
import { Card, EmptyState } from '../Card.js';
import { ErrorBanner, Spinner } from '../FormBits.js';
import type { ExportDatasetInfo } from '@agio/core/domain';

const GROUPS: { kind: ExportDatasetInfo['kind']; title: string; hint: string }[] = [
  { kind: 'raw', title: 'נתונים גולמיים', hint: 'כפי שנקראו מקובצי האקסל ומדוחות הבנק, לכל התיקים' },
  { kind: 'derived', title: 'נתונים מחושבים', hint: 'מה שהמערכת חישבה בזמן הקליטה, לכל התיקים' },
  { kind: 'summary', title: 'סיכום', hint: 'התיק הכולל — אותם מספרים שמוצגים בגרפי הסיכום בפורטל' },
];

/**
 * ייצוא נתונים: for the client picked in the header, download any dataset as a CSV that opens in
 * Excel. One file per dataset, every fund of the client in it — the server
 * builds the file; this screen only fetches it and hands it to the browser.
 */
export function ExportCard() {
  const clientId = useRecoilValue(activeClientState) ?? '';
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { data: datasets, error: listError } = useApi<ExportDatasetInfo[]>('/admin/export/datasets');

  async function download(id: string) {
    setBusy(id);
    setError(null);
    try {
      // Through api.blob, not a plain link: local dev authenticates with a header a link cannot carry.
      const blob = await api.blob(`/admin/clients/${encodeURIComponent(clientId)}/export/${id}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${clientId}-${id}.csv`;
      a.click();
      // Revoking in the same tick can cancel the download in Safari and Firefox.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <div className="grid gap-5">
        {!clientId && <EmptyState label="בחרו לקוח למעלה" />}
        <ErrorBanner error={error ?? listError} title="ההורדה נכשלה" />
        {clientId && datasets && (
          <>
            <p className="text-sm text-muted-foreground">
              קובצי CSV שנפתחים באקסל. אחוזים ותשואות מופיעים כשבר עשרוני:{' '}
              <span dir="ltr" className="inline-block">0.0129 = 1.29%</span>
            </p>
            {GROUPS.map((g) => (
              <section key={g.kind} className="grid gap-2">
                <div>
                  <h2 className="font-semibold">{g.title}</h2>
                  <p className="text-xs text-muted-foreground">{g.hint}</p>
                </div>
                <ul className="grid gap-2 sm:grid-cols-2">
                  {datasets.filter((d) => d.kind === g.kind).map((d) => (
                    <li key={d.id}>
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => download(d.id)}
                        className="flex w-full items-center justify-between gap-3 rounded-lg border border-border px-4 py-3 text-start text-sm transition-colors hover:bg-muted/60 disabled:opacity-60"
                      >
                        <span>{d.labelHe}</span>
                        <span className="flex items-center gap-2 text-xs text-muted-foreground">
                          {busy === d.id && <Spinner />}
                          CSV ⬇
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </>
        )}
      </div>
    </Card>
  );
}
